import { Suspense, lazy, type ComponentType } from "react";
import { createBrowserRouter } from "react-router-dom";
import {
  PUBLIC_INDEXABLE_ROUTE_DEFINITIONS,
  PUBLIC_LOCALES,
  buildLocalizedPublicPath,
} from "@/config/publicSiteManifest.js";
import { ensurePublicTranslations } from "@/i18n/publicI18n";
import PublicMachineModeShell from "@/components/public/PublicMachineModeShell";
import NotFound from "./pages/NotFound";

const createPublicLazyRoute = <T extends ComponentType<Record<string, unknown>>>(
  importFn: () => Promise<{ default: T }>,
) => {
  const LazyComponent = lazy(importFn);

  return (props: Record<string, unknown>) => (
    <Suspense fallback={null}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

const publicRouteComponents: Record<string, ComponentType> = {
  homepage: createPublicLazyRoute(() => import("./pages/Homepage")),
  manifesto: createPublicLazyRoute(() => import("./pages/Manifesto")),
  pledge: createPublicLazyRoute(() => import("./pages/Pledge")),
  privacy: createPublicLazyRoute(() => import("./pages/Privacy")),
  terms: createPublicLazyRoute(() => import("./pages/Terms")),
  urbanismo: createPublicLazyRoute(() => import("./pages/Urbanismo")),
  condominios: createPublicLazyRoute(() => import("./pages/Condominios")),
  pmeToolkit: createPublicLazyRoute(() => import("./pages/PMEToolkit")),
};

const toRouterPath = (routePath: string) => {
  if (routePath === "/") {
    return "/";
  }

  return routePath.replace(/^\/+/, "");
};

const publicRoutes = PUBLIC_INDEXABLE_ROUTE_DEFINITIONS.flatMap((route) => {
  const Component = publicRouteComponents[route.id];

  if (!Component) {
    return [];
  }

  return PUBLIC_LOCALES.map((locale) => ({
    path: toRouterPath(buildLocalizedPublicPath(route.basePath, locale)),
    loader: async () => {
      await ensurePublicTranslations(
        locale,
        route.id as Parameters<typeof ensurePublicTranslations>[1],
      );

      return null;
    },
    element: (
      <PublicMachineModeShell>
        <Component />
      </PublicMachineModeShell>
    ),
  }));
});

export const publicRouter = createBrowserRouter([
  ...publicRoutes,
  {
    path: "*",
    element: <NotFound />,
  },
]);
